/**
 * A template's lines, for this roof and these answers, ready to plan.
 *
 * Two screens turn a template into orders: the estimate builder, which writes a
 * whole package, and "From Measurements" on the orders, which writes only the
 * quantities (Jonathon, 2026-08-24). Both hand the result to `generatePlan`.
 * What goes in *before* that — which lines apply, which part each line is in
 * the finish that was chosen, how much of a girth-sized line to cut — is a
 * business rule, and it used to live in the estimate builder's component.
 *
 * So the order screen never got it. Once the metal panel templates carried a
 * 2" screw for a roof on furring strips and a 1 1/2" one for a roof without,
 * an order from measurements ordered both, plus the strips, the nails, the
 * furring labour and the touch-up paint whatever was asked, every part painted
 * — a rule implemented in one screen is a rule the other bypasses (CLAUDE.md
 * rule 5). This is that rule, once, for both.
 *
 * Nothing here touches money. It decides which lines and which products; the
 * generator works out quantities and the ladder does the arithmetic.
 */

import {
  applyFinish,
  exposedFastenerTokens,
  exposedFastenerWarnings,
  finishIndex,
  headlineInFinish,
  isExposedFastenerType,
  metalScopeNote,
  type ExposedFastenerAnswers,
  type FinishTwinned,
  type MetalScopeTemplate,
} from "./exposedFastener.js";
import {
  flatRoofTokens,
  girthQuantity,
  isFlatRoofType,
  lineApplies,
  type FlatRoofAnswers,
} from "./flatRoof.js";
import {
  deckTokens,
  defaultDeckAnswers,
  isDeckType,
  withDeckCounts,
  type DeckAnswers,
} from "./deck.js";
import { deckCounts } from "./deckCounts.js";
import { deckScopeNote, deckWarnings } from "./deckScope.js";
import type { RoofMeasurements, RoofTemplateItem } from "./generator.js";
import { isSidingType } from "./siding.js";
import {
  defaultSidingAnswers,
  sidingMeasurementWarnings,
  sidingScopeNote,
  sidingTokens,
  withSidingCounts,
  type SidingAnswers,
} from "./sidingAnswers.js";
import { isBoughtIn, isWoodBonus, laborOnlyScope } from "./laborOnly.js";

/** The parts of a template the lines depend on. */
export interface PlanTemplate extends MetalScopeTemplate {
  roof_type?: string | null;
  headline_product_id?: string | null;
}

/** A catalogue product, as far as choosing lines is concerned. */
export interface PlanProduct extends FinishTwinned {
  /** For naming a painted part that has no galvalume version. */
  name?: string | null;
  /** Which order the product lands on, for labor only. See `isBoughtIn`. */
  category?: string | null;
  kind?: string | null;
}

/**
 * Every answer the generating screens collect. Of the three sets of questions
 * only one is read; labor only applies to any template (ADR 0240).
 */
export interface TemplateAnswers {
  flat: FlatRoofAnswers;
  metal: ExposedFastenerAnswers;
  /**
   * The siding questions. Optional because every estimate generated before
   * 2026-10-01 recorded only the two above, and replaying one of those must
   * still work; a siding template read without them gets the defaults.
   */
  siding?: SidingAnswers;
  /**
   * The deck questions: its size, height, steps and railing. Optional for the
   * same reason — nothing generated before 2026-10-11 recorded them.
   */
  deck?: DeckAnswers;
  /**
   * The customer buys the materials: nothing is ordered, and the scope says
   * so. Optional because answers stored before 2026-09-30 do not carry it,
   * and they are read back into this function.
   */
  laborOnly?: boolean;
}

/** Which questions a template asks before it is generated. */
export type TemplateQuestions = "flat" | "metal" | "siding" | "deck" | "none";

export function templateQuestions(
  roofType: string | null | undefined,
): TemplateQuestions {
  if (isFlatRoofType(roofType)) return "flat";
  if (isExposedFastenerType(roofType)) return "metal";
  if (isSidingType(roofType)) return "siding";
  if (isDeckType(roofType)) return "deck";
  return "none";
}

export interface LinesToPlan<R> {
  /** Which questions were read. */
  questions: TemplateQuestions;
  /** The lines to hand to `generatePlan`, in the products they resolve to. */
  items: RoofTemplateItem[];
  /** The roofs to hand to it — a flat roof carries its insulation answer. */
  roofs: (R & Pick<RoofMeasurements, "wall_insulation">)[];
  /** The product the customer's headline line names. */
  headlineProductId: string | null;
  /**
   * The scope of work to write, or undefined for the template's own.
   *
   * Only a metal panel roof assembles one, from its finish-neutral sections,
   * the answers and the warranty for the finish chosen. See `metalScopeNote`.
   */
  scopeNote: string | null | undefined;
  /** Said out loud before the order goes out. Never a reason to stop. */
  warnings: string[];
}

/**
 * A quantity for a line cut from a 4x10 sheet.
 *
 * Coping and drip edge have no coverage a product could hold — the same coil
 * is 40 feet of one profile and 30 of another — so the line names which girth
 * to ask for and the answer supplies it. A line that cannot be worked out is
 * left exactly as it was, which sends it to `skipped` with a reason rather
 * than ordering none.
 */
function withGirthQuantity<I extends RoofTemplateItem>(
  item: I,
  answers: FlatRoofAnswers,
  roofs: readonly RoofMeasurements[],
): I {
  const source = item.girth_source;
  if (source !== "termination" && source !== "coping") return item;
  const total = roofs.reduce(
    (sum, roof) => sum + (girthQuantity(source, answers, roof) ?? 0),
    0,
  );
  return total > 0 ? { ...item, qty: total } : item;
}

/**
 * The lines a template produces on this roof, given the answers.
 *
 *   - A flat roof keeps the lines its build calls for (`applies_when`), sizes
 *     the girth lines from the answers, and carries the wall insulation answer
 *     on the roof, where the slot resolver reads it.
 *   - A metal panel roof keeps the lines its furring answer calls for, then
 *     swaps every painted part for its galvalume twin when galvalume was
 *     chosen — after selection and before pricing, so the swapped product's own
 *     cost, coverage and measurement are what the generator reads. Its headline
 *     follows the finish, and it says which painted parts had no twin.
 *   - A siding template keeps the lines its answers call for and writes the
 *     rep's counts onto the accessory lines (siding.ts).
 *   - Everything else is the template as it stands. No template outside those
 *     three carries a condition.
 *
 * Then, on any template, labor only drops every line that would be bought
 * from a supplier and the wood bonus wording, and puts the labor-only sentence
 * above the scope.
 */
export function linesToPlan<R extends RoofMeasurements>(
  template: PlanTemplate | undefined,
  items: readonly RoofTemplateItem[],
  catalogue: readonly PlanProduct[],
  roofs: readonly R[],
  answers: TemplateAnswers,
): LinesToPlan<R> {
  const planned = linesForBuild(template, items, catalogue, roofs, answers);
  if (!answers.laborOnly) return planned;

  const byId = new Map(catalogue.map((product) => [product.id, product]));
  return {
    ...planned,
    items: planned.items.filter((item) => {
      // Wording is not bought, and a line whose product has gone is left for
      // the generator to report rather than vanishing here. The wood bonus is
      // the one piece of wording that goes: it promises the customer free
      // decking they are now buying themselves (ADR 0255).
      if ((item.section ?? "").trim() !== "") return !isWoodBonus(item.section);
      const product = item.product_id ? byId.get(item.product_id) : undefined;
      return !product || !isBoughtIn(product);
    }),
    scopeNote: laborOnlyScope(
      planned.scopeNote === undefined ? template?.scope : planned.scopeNote,
    ),
  };
}

function linesForBuild<R extends RoofMeasurements>(
  template: PlanTemplate | undefined,
  items: readonly RoofTemplateItem[],
  catalogue: readonly PlanProduct[],
  roofs: readonly R[],
  answers: TemplateAnswers,
): LinesToPlan<R> {
  const questions = templateQuestions(template?.roof_type);
  const headline = template?.headline_product_id ?? null;

  if (questions === "none") {
    return {
      questions,
      items: [...items],
      roofs: [...roofs],
      headlineProductId: headline,
      scopeNote: undefined,
      warnings: [],
    };
  }

  if (questions === "flat") {
    const tokens = flatRoofTokens(answers.flat);
    return {
      questions,
      items: items
        .filter((item) => lineApplies(item.applies_when, tokens))
        .map((item) => withGirthQuantity(item, answers.flat, roofs)),
      roofs: roofs.map((roof) => ({
        ...roof,
        wall_insulation: answers.flat.wallInsulation,
      })),
      headlineProductId: headline,
      scopeNote: undefined,
      warnings: [],
    };
  }

  if (questions === "siding") {
    /*
     * A siding template is asked what the house gets — tear-off or layover,
     * wrapped openings, soffit, fascia, frieze — and how many of each counted
     * accessory. The first set selects lines; the second writes quantities.
     * The scope the customer reads is the template's with the answers under
     * it, the way a metal roof's carries its finish (siding.ts).
     */
    const siding = answers.siding ?? defaultSidingAnswers(roofs);
    const tokens = sidingTokens(siding);
    return {
      questions,
      items: withSidingCounts(
        items.filter((item) => lineApplies(item.applies_when, tokens)),
        siding,
      ),
      roofs: [...roofs],
      headlineProductId: headline,
      scopeNote: sidingScopeNote(template?.scope, siding),
      warnings: sidingMeasurementWarnings(siding, roofs),
    };
  }

  if (questions === "deck") {
    /*
     * A deck is not measured on anything: the rep types its size, and every
     * line's quantity is a count the size works out by rule (deck.ts). A
     * second-story deck pays the crew more, and whether it is fixed to the
     * house decides the ledger, its flashing and its tension ties. The scope
     * is written from the answers (deckScope.ts).
     */
    const deck = answers.deck ?? defaultDeckAnswers();
    const tokens = deckTokens(deck);
    return {
      questions,
      items: withDeckCounts(
        items.filter((item) => lineApplies(item.applies_when, tokens)),
        deckCounts(deck, template?.roof_type),
      ),
      roofs: [],
      headlineProductId: headline,
      scopeNote: deckScopeNote(template?.scope, deck, template?.roof_type),
      warnings: deckWarnings(deck),
    };
  }

  const tokens = exposedFastenerTokens(answers.metal);
  const selected = items.filter((item) =>
    lineApplies(item.applies_when, tokens),
  );
  const index = finishIndex(catalogue);
  const finished = applyFinish(selected, answers.metal.finish, index);
  const names = new Map(catalogue.map((product) => [product.id, product.name]));

  return {
    questions,
    items: finished.items,
    roofs: [...roofs],
    headlineProductId: headlineInFinish(headline, answers.metal.finish, index),
    scopeNote: metalScopeNote(template, answers.metal),
    warnings: exposedFastenerWarnings(
      answers.metal,
      finished.missing,
      template?.scope,
      (id) => names.get(id),
    ),
  };
}
